import { useCallback } from 'react'
import { create } from 'zustand'

import { getInventoryApi } from '@api'
import { consumeCardService, updateUserSkinService } from '@services'

import { createSelectors, Mapper } from './helpers'

interface InventoryStore {
  isInitialized?: boolean
  isOpen: boolean
  mode: InventoryMode
  activeTab: number
  items: InventoryCard[]
  dropZones: DropZone[]
  tooltipVisible: boolean
  newCards: InventoryCard[]
  shownCardIds: string[]
  actions: {
    toggle: (isOpen?: boolean) => void
    setActiveTab: (activeTab: number) => void
    setMode: (mode: InventoryMode) => void
    reserveCard: (card: InventoryCard) => void
    useCard: (
      card: InventoryCard,
      additionalData?: Record<string, any>
    ) => Promise<void>
    clearReservedCards: () => void
    useReservedCard: () => void
    addDropZones: (dropZones: DropZone[]) => void
    removeDropZones: (ids: string[]) => void
    addCard: (card: InventoryCard) => void
    clearNewCard: () => void
    hideTooltip: () => void
    getItems: () => void
  }
}

export const useInventoryStoreBase = create<InventoryStore>((set, get) => ({
  isInitialized: false,
  isOpen: false,
  mode: 'fullscreen',
  activeTab: 0,
  items: [],
  dropZones: [],
  tooltipVisible: localStorage.getItem('isTooltipHidden') !== 'true',
  newCards: [],
  shownCardIds: [],
  actions: {
    getItems: async () => {
      const { data, ok } = await getInventoryApi()

      if (ok && data) {
        const inventoryItems = data.map((item) =>
          MainInventoryMapper.mapFromBackend(item)
        )
        set((prev) => {
          if (!prev.isInitialized) {
            return { items: inventoryItems, isInitialized: true }
          }

          const newCards = inventoryItems.filter(
            (item) =>
              !prev.items.find((el) => el.id === item.id) &&
              !prev.shownCardIds.includes(item.id)
          )
          const res = {
            items: inventoryItems,
          } as InventoryStore

          if (newCards.length) {
            const existingQueueIds = new Set(prev.newCards.map((c) => c.id))
            const newQueueCards = newCards.filter(
              (c) => !existingQueueIds.has(c.id)
            )
            if (newQueueCards.length > 0) {
              res.newCards = [...prev.newCards, ...newQueueCards]
            }
          }

          return res
        })
      }
    },
    hideTooltip: () => {
      set({ tooltipVisible: false })
      localStorage.setItem('isTooltipHidden', 'true')
    },
    toggle: (isOpen) => {
      const value = isOpen === undefined ? !get().isOpen : isOpen
      set({ isOpen: value })
      if (value) {
        get().actions.hideTooltip()
      }
    },
    setActiveTab: (activeTab) => {
      set({ activeTab })
    },
    setMode: (mode) => {
      set({ mode })
    },
    useCard: async (
      card: InventoryCard,
      additionalData?: Record<string, any>
    ) => {
      if (SKINS_TYPES.includes(card.displayType)) {
        await updateUserSkinService(card)
        return
      }
      const { ok, data } = await consumeCardService(card, additionalData);
      data?.reward_items.forEach(item => {
        get().actions.addCard(MainInventoryMapper.mapFromBackend(item))
      })
      if (ok) {
        set({
          items: get().items.filter((item) => item.id !== card.id),
        })
      }
    },
    reserveCard: (card: InventoryCard) => {
      set({
        items: get().items.map((item) =>
          item.id === card.id ? { ...item, isReserved: true } : item
        ),
      })
    },
    clearReservedCards: () => {
      set({
        items: get().items.map((item) => ({ ...item, isReserved: false })),
      })
    },
    useReservedCard: () => {
      set({
        items: get().items.map((item) =>
          item.isReserved ? { ...item, isUsed: true, isReserved: false } : item
        ),
      })
    },
    addDropZones: (dropZones: DropZone[]) => {
      set({ dropZones: [...get().dropZones, ...dropZones] })
    },
    removeDropZones: (ids: string[]) => {
      set({
        dropZones: get().dropZones.filter((el) => !ids.includes(el.id)),
      })
    },
    addCard: (card: InventoryCard) => {
      const state = get()
      const alreadyShown = state.shownCardIds.includes(card.id)
      const alreadyInQueue = state.newCards.some((c) => c.id === card.id)
      set({
        newCards:
          alreadyShown || alreadyInQueue
            ? state.newCards
            : [...state.newCards, card],
        items: state.items.some((c) => c.id === card.id)
          ? state.items
          : [...state.items, card],
      })
    },
    clearNewCard: () => {
      const dismissed = get().newCards[0]
      set({
        newCards: get().newCards.slice(1),
        ...(dismissed && {
          shownCardIds: [...get().shownCardIds, dismissed.id],
        }),
      })
    },
  },
}))

export const useInventoryActions = () =>
  useInventoryStoreBase((state) => state.actions)

export const useInventory = createSelectors(useInventoryStoreBase)

const useGetCards = ({
  displayTypes,
  excludeTypes,
}: {
  displayTypes?: InventoryCardDisplayType[]
  excludeTypes?: InventoryCardDisplayType[]
}) => {
  return useInventoryStoreBase((state) => {
    return state.items.filter((item) => {
      if (excludeTypes?.includes(item.displayType)) {
        return false
      }
      if (item.displayType === 'card') {
        return false
      }
      if (item.isUsed || item.isReserved) {
        return false
      }
      if (displayTypes) {
        return displayTypes.includes(item.displayType)
      }
      return true
    })
  })
}
export const REFILLS_QUANTITY: Record<string, number> = {
  '019b3d56-be10-7d7e-b572-ebe4eb7996ed': 1,
  '0197b173-baa8-7cce-94c8-45ded7ffeb57': 3,
  '0197b158-3413-7e4b-b667-92a3765eef93': 5,
  '0197639e-9e02-7a5b-add7-79c99e607f34': 10,
}

export const SKINS_TYPES: InventoryCardDisplayType[] = [
  'avatar',
  'avatar_shell',
  'star_effect',
  'spiral_effect',
]
const QUEST_TYPES: InventoryCardDisplayType[] = ['quest_item', 'note']
export const useGetCardsSkins = () => {
  return useGetCards({ displayTypes: SKINS_TYPES }).sort(
    (a, b) => b.rarity - a.rarity
  )
}

export const useGetCardsQuest = () => {
  return useGetCards({ displayTypes: QUEST_TYPES })
}

export const useGetBoosters = () => {
  return useGetCards({ excludeTypes: [...QUEST_TYPES, ...SKINS_TYPES] })
}

export const useGetDropZones = (availableItems: string[]) => {
  return useInventoryStoreBase((state) =>
    state.dropZones.filter((el) =>
      el.availableItems.some((item) => availableItems.includes(item))
    )
  )
}

export type InventoryCardType =
  | 'text'
  | 'thing'
  | 'booster'
  | 'bundle'
  | 'empty'
export type InventoryMode = 'fullscreen' | 'minimized'
export type InventoryCardDisplayType =
  | 'booster'
  | 'lootbox'
  | 'chest'
  | 'card'
  | 'note'
  | 'quest_item'
  | 'avatar'
  | 'avatar_shell'
  | 'star_effect'
  | 'spiral_effect'
export type InventoryType = 'quest' | 'skins' | 'items'
export const INVENTORY_RARITY = [
  'unknown',
  'common',
  'rare',
  'epic',
  'legendary',
] as const

export type InventoryCard = {
  id: string
  type: InventoryCardType
  title?: string
  text?: string
  author?: string
  iconName?: string
  isUsed?: boolean
  isReserved?: boolean
  appFiles: string
  appMeta?: string
  collection: string
  createdAt: string
  description: string
  displayType: InventoryCardDisplayType
  gameOnly: boolean
  hidden: boolean
  iconUrl: string
  itemDefId: string
  marketable: boolean
  quantity?: number
  serial?: number
  tags: string
  tradable: boolean
  updatedAt: string
  rarity: number
  defId: string
}

export type MainInventoryBackendCard = {
  app_files: string
  app_meta?: string
  collection: string
  created_at: string
  description: string
  display_type: InventoryCardDisplayType
  game_only: boolean
  hidden: boolean
  icon_url: string
  item_def_id: string
  item_id?: string
  marketable: boolean
  name: string
  quantity?: number
  serial?: number
  tags: string
  tradable: boolean
  type: string
  rarity: number
  updated_at: string
}

export const MainInventoryMapper: Mapper<
  MainInventoryBackendCard,
  InventoryCard
> = {
  mapFromBackend: function (data) {
    return {
      id: data.item_id || '',
      // TODO: investigate and remove
      type: QUEST_TYPES.includes(data.display_type) ? 'thing' : 'booster',
      title: data.name,
      text: data.description,
      author: data.name,
      iconName: data.icon_url,
      isUsed: false,
      isReserved: false,
      rarity: data.rarity,
      appFiles: data.app_files,
      appMeta: data.app_meta || '',
      collection: data.collection,
      createdAt: data.created_at,
      description: data.description,
      displayType: data.display_type,
      gameOnly: data.game_only,
      hidden: data.hidden,
      iconUrl: data.icon_url,
      itemDefId: data.item_def_id,
      marketable: data.marketable,
      quantity: data.quantity,
      serial: data.serial,
      tags: data.tags,
      tradable: data.tradable,
      updatedAt: data.updated_at,
      defId: data.item_def_id,
    }
  },
  mapToBackend(data) {
    return {
      app_files: '',
      app_meta: '',
      collection: '',
      created_at: '',
      description: '',
      display_type: 'booster',
      game_only: false,
      hidden: false,
      icon_url: '',
      item_def_id: '',
      item_id: '1',
      marketable: false,
      name: '',
      quantity: 2,
      serial: 3,
      tags: '',
      tradable: false,
      type: '',
      rarity: 0,
      updated_at: '',
    }
  },
}

export type DropZone = {
  availableItems: string[]
  onDropItem: (item: InventoryCard) => void
  id: string
  rect: DOMRect
}

export const useInitializeInventory = () => {
  // const { t } = useTranslation()
  const { getItems } = useInventoryActions()

  const init = useCallback(() => {
    getItems()
  }, [getItems])

  return { init }
}
